import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './product-image-grid.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import type { LightboxAsset, LightboxMediaItem } from 'components/discovery/pdp/lightbox/interfaces.js'
import { mapLightboxAssets, mapPdpProductToLightboxMediaItems } from 'components/discovery/pdp/lightbox/lightbox-assets.js'
import {
  badgePositions,
  type BadgePositionType,
} from 'components/discovery/pdp/lightbox/lightbox.js'
import { CSCLightboxModal } from 'components/discovery/pdp/lightbox/components/lightbox-modal/lightbox-modal.js'
import 'components/discovery/pdp/lightbox/lightbox.js'
import 'components/discovery/pdp/lightbox/components/lightbox-modal/lightbox-modal.js'
import 'components/discovery/pdp/lightbox/components/lightbox-media/lightbox-media.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'

export const productImageGridHiddenFields = ['modal-title'] as const

export { badgePositions }
export type { BadgePositionType }

const jsonArrayConverter = {
  fromAttribute(value: string | null): unknown[] {
    if (value && value.length > 0) {
      return JSON.parse(decodeURI(value))
    }
    return []
  },
}

const MAX_GRID_TILES = 6

function isFullWidthIndex(index: number): boolean {
  if (index === 0) return true
  return (index - 1) % 3 === 2
}

@customElement('csc-product-image-grid')
export class CSCProductImageGrid extends HiddenFieldsMixin(LitElement, productImageGridHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: Array, converter: jsonArrayConverter })
  assets: LightboxAsset[] = []

  @property({ type: String, attribute: 'badge-position' })
  badgePosition: BadgePositionType = 'top-left'

  @property({ type: String, attribute: 'product-title' })
  productTitle = ''

  @property({ type: String, attribute: 'fallback-source' })
  fallbackSource = ''

  /** Product loaded by csc-pdp-provider, its media replace the `assets` attribute */
  @state()
  private pdpProduct: PdpProduct | null = null

  @state()
  private mediaItems: LightboxMediaItem[] = []

  @state()
  private gridImages: LightboxMediaItem[] = []

  @state()
  private activeIndex = 0

  @state()
  private smallScreen = false

  private mediaQuery: MediaQueryList | null = null

  @query('csc-lightbox-modal')
  private modal!: CSCLightboxModal

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (!product) return
      if (!this.productTitle) this.productTitle = product.name
      if (!this.fallbackSource) this.fallbackSource = product.default_picture?.large ?? ''
      this.pdpProduct = product
    })
  }

  override connectedCallback() {
    super.connectedCallback()
    this.mediaQuery = window.matchMedia(`(max-width: ${breakpoints.small}px)`)
    this.smallScreen = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.onMediaChange)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.mediaQuery?.removeEventListener('change', this.onMediaChange)
  }

  private onMediaChange = (event: MediaQueryListEvent) => {
    this.smallScreen = event.matches
  }

  override willUpdate(changedProperties: Map<string, unknown>) {
    super.willUpdate(changedProperties)
    if (changedProperties.has('assets') || changedProperties.has('pdpProduct') || changedProperties.has('fallbackSource')) {
      this.syncGridImages()
    }
  }

  private syncGridImages() {
    this.mediaItems = this.pdpProduct
      ? mapPdpProductToLightboxMediaItems(this.pdpProduct, this.fallbackSource)
      : mapLightboxAssets(this.assets, this.fallbackSource)

    this.gridImages = this.mediaItems.filter((item) => item.kind !== 'video')

    if (this.activeIndex >= this.gridImages.length) {
      this.activeIndex = Math.max(0, this.gridImages.length - 1)
    }
  }

  private onTileClick(index: number) {
    const hasIndexChanged = index !== this.activeIndex

    this.activeIndex = index
    if (this.modal) {
      this.modal.activeIndex = index
      this.modal.open()
    }

    if (hasIndexChanged) {
      this.dispatchEvent(new CustomEvent('product-image-grid-open', {
        detail: { index },
        bubbles: true,
        composed: true,
      }))
    }
  }

  private onModalClose() {
    this.dispatchEvent(new CustomEvent('product-image-grid-close', { bubbles: true, composed: true }))
  }

  private onModalSlideChange(event: CustomEvent<{ index: number }>) {
    this.activeIndex = event.detail.index
  }

  private renderTags() {
    return html`
      <div class="${classMap({
        'badge-group': true,
        [`position-${this.badgePosition}`]: true,
      })}"><slot name="badges"></slot></div>
    `
  }

  private renderTile(item: LightboxMediaItem, index: number) {
    const isMain = index === 0

    return html`
      <div
        class="${classMap({
          tile: true,
          full: isFullWidthIndex(index),
        })}"
        @click="${() => this.onTileClick(index)}"
      >
        ${isMain ? this.renderTags() : nothing}
        <csc-lightbox-media .item="${item}"></csc-lightbox-media>
      </div>
    `
  }

  private renderGrid() {
    return html`
      <div class="csc-product-image-grid">
        ${this.gridImages.slice(0, MAX_GRID_TILES).map((item, index) => this.renderTile(item, index))}
      </div>

      <csc-lightbox-modal
        .images="${this.gridImages}"
        active-index="${this.activeIndex}"
        product-title="${this.productTitle}"
        hidden-fields="${this.isHidden('modal-title') ? 'title' : ''}"
        @lightbox-modal-close="${this.onModalClose}"
        @lightbox-modal-slide-change="${this.onModalSlideChange}"
      ></csc-lightbox-modal>
    `
  }

  private renderMobile() {
    return html`
      <csc-lightbox
        .assets="${this.assets}"
        badge-position="${this.badgePosition}"
        product-title="${this.productTitle}"
        fallback-source="${this.fallbackSource}"
        hidden-fields="${this.isHidden('modal-title') ? 'modal-title' : ''}"
      >
        <slot name="badges" slot="badges"></slot>
      </csc-lightbox>
    `
  }

  override render() {
    if (this.smallScreen) {
      if (this.mediaItems.length === 0) {
        return nothing
      }

      return this.renderMobile()
    }

    if (this.gridImages.length === 0) {
      return nothing
    }

    return this.renderGrid()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-image-grid': CSCProductImageGrid
  }
}
